import 'jspdf-autotable';
import logo from './assets/clearfxai-logo.svg';
import * as utils from "./utils.js";
import i18n from './i18n/i18n.js';
import { formatReportQuoteStatus } from './components/reports/reportHelpers.js';

export function generateOPCSV(reportsData, typeOfReport) {
  const rows = [];

  // Header
  rows.push([
    'ID',
    i18n.t('quoteInfo.alias'),
    i18n.t('quoteInfo.dealTime'),
    i18n.t('quoteInfo.ccy'),
    i18n.t('quoteInfo.amountCcy'),
    i18n.t('quoteInfo.clientRate'),
    'RFQ ID',
    'Side',
    i18n.t('quoteInfo.status'),
    i18n.t('broker.labels.transactionPurpose')

  ]);
  reportsData.forEach(item => {
      const dealTime = item.rfq_timecreate ?? item.rfq_deal_time ?? item.timecreate
      rows.push([
        item.id,
        item.alias,
        dealTime ? utils.formatTimeAndDate(dealTime) : '-',
        item.ccy,
        utils.formatPrice(item.qty),
        item.rfq_final_rate != null ? Number(item.rfq_final_rate).toFixed(4) : '-',
        item.rfqId,
        item.side,
        item.status,
        item.operationType

      ]);
  });

  // Convert to CSV string
  const csvContent = rows
    .map(row => row.map(val => `"${val ?? ''}"`).join(','))
    .join('\n');

  // Download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${typeOfReport}_fx_${new Date().toISOString().slice(0,10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function generateRFQCSV(reportsData) {
  const rows = [];
  const role = localStorage.getItem('userRole');
  const isBroker = role === 'broker';
  const isPro = role === 'pro';

  const now = new Date();
  const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // Header (merged from both tables)
  const headers = [];
  if (isBroker) {
    headers.push(i18n.t('reports.partnerName'));
  }
  headers.push(
    i18n.t('quoteInfo.clientName'),
    i18n.t('quoteInfo.dealTime'),
    i18n.t('quoteInfo.ccy'),
    i18n.t('quoteInfo.amountCcy'),
    i18n.t('quoteInfo.clientRate'),
    i18n.t('quoteInfo.volumeCcyBrl'),
    i18n.t('quoteInfo.settlement'),
    i18n.t('quoteInfo.id'),
    i18n.t('quoteInfo.status'),
    i18n.t('quoteInfo.bankName')
  );
  if (!isPro) headers.push(i18n.t('quoteInfo.bankRate'));
  headers.push(isBroker ? i18n.t('quoteInfo.brokerMarkup') : i18n.t('quoteInfo.spread'));
  if (isBroker) {
    headers.push(i18n.t('quoteInfo.partnerSpread'));
  }
  headers.push(i18n.t('quoteInfo.bankLastUpdate'));
  rows.push(headers);

  reportsData.forEach((item) => {
    const quotes = item.rfq_lastquote?.quote_list || [];
    const companyName = item.company_name || item.company?.name || item.company?.id || '-';
    const partnerName = item.partner?.name || item.partner?.id || '-';

    // If NO quotes → still push RFQ info
    if (quotes.length === 0) {
      const row = [];
      if (isBroker) {
        row.push(partnerName);
      }
      row.push(
        companyName,
        utils.formatTimeAndDate(item.rfq_deal_time),
        item.rfq_ccy,
        utils.formatPrice(item.rfq_amount),
        item.rfq_final_rate != null ? Number(item.rfq_final_rate).toFixed(4) : '-',
        item.volume_brl != null && Number.isFinite(Number(item.volume_brl)) ? `R$ ${utils.formatBRLPretty(Number(item.volume_brl))}` : '-',
        item.rfq_security !== 'FXNDF' ? item.rfq_spotsett?.toUpperCase() : '-',
        item.rfq_id,
        '-', '-'
      );
      if (!isPro) row.push('-');
      row.push('-');
      if (isBroker) {
        row.push('-');
      }
      row.push('-');
      rows.push(row);
    } else {
      // Each quote = one row
      quotes.forEach((rfq) => {
        const row = [];
        if (isBroker) {
          row.push(partnerName);
        }
        row.push(
          companyName,
          utils.formatTimeAndDate(item.rfq_deal_time),
          item.rfq_ccy,
          utils.formatPrice(item.rfq_amount),
          item.rfq_final_rate != null ? Number(item.rfq_final_rate).toFixed(4) : '-',
          item.volume_brl != null && Number.isFinite(Number(item.volume_brl)) ? `R$ ${utils.formatBRLPretty(Number(item.volume_brl))}` : '-',
          item.rfq_security !== 'FXNDF' ? item.rfq_spotsett?.toUpperCase() : '-',
          item.rfq_id,
          formatReportQuoteStatus(rfq, item.rfq_status),
          rfq.bank_label
        );
        if (!isPro) row.push(rfq.quote_bank_rate);
        row.push(isBroker ? rfq.broker_markup_str : (role === 'partner' ? rfq.spread_avg_spot : '-'));
        if (isBroker) {
          row.push(item.partner ? rfq.partner_markup_str : '-');
        }
        row.push(
          rfq.deal ? utils.formatTimeAndDate(rfq.quote_bank_time) : '-'
        );
        rows.push(row);
      });
    }
  });

  // Convert to CSV string
  const csvContent = rows
    .map(row => row.map(val => `"${val ?? ''}"`).join(','))
    .join('\n');

  // Download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `report_fx_${formattedDate}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
